import { POPULATION_TYPE, SensitivityLevelHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { EntitiesModalProps } from "@/components/modals/entities-table-modal/model";
import { cord, cord3 } from "@/gql/lib";
import { AxisType } from "@/gql/types";

const sensitivities_str = (sensitivities: number[]) => {
  switch (true) {
    case sensitivities.length < 4 && sensitivities.length > 1:
      return (
        sensitivities
          .map((level) => SensitivityLevelHelper.intToString(level))
          .join(",") + " sensitivity"
      );
    case sensitivities.length == 1:
      const level = sensitivities[0];
      if (level === 3) {
        return "Highly sensitive";
      } else {
        return SensitivityLevelHelper.intToString(level) + " sensitive";
      }
    case sensitivities.length == 0:
    case sensitivities.length == 4:
    default:
      return "";
  }
};

const from_cloud_folders = (
  item: any,
  sensitivities: number[]
): EntitiesModalProps => {
  const sensitive = sensitivities_str(sensitivities);
  const { type, from } = SharedState.selectedTimeFrameOption.value;
  return {
    info_types: [item.id],
    type: POPULATION_TYPE.FOLDER,
    title: `Cloud Folders containing <b>${sensitive}</b> <info_type> information`,
    filter_sensitivities: sensitivities,
    coordinate: {
      x: cord(AxisType.Folder),
      y: {
        type: AxisType.ContentLabel,
        values: item.labels,
      },
      z: {
        type,
        values: [from],
      },
    },
    files_title: (entity, label_name) =>
      `<b>${sensitive} ${label_name}</b> Information files that <b>${entity.name}</b> has access`,
    events: {
      title: (entity, label_name) =>
        `Events related to <b>${entity.name}</b> containing <b>${sensitive} ${label_name}</b> information`,
      coordinate: (entity, label_id) => {
        return {
          x: cord3(AxisType.Folder, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: label_id ? [label_id] : item.labels,
          },
          z: {
            type,
            values: [from],
          },
        };
      },
    },
  };
};

const from_employees = (
  item: any,
  sensitivities: number[]
): EntitiesModalProps => {
  const { type, from } = SharedState.selectedTimeFrameOption.value;
  const sensitive = sensitivities_str(sensitivities);
  return {
    info_types: [item.id],
    type: POPULATION_TYPE.EMPLOYEE,
    title: `Employees that shared <b>${sensitive}</b> <info_type> information`,
    filter_sensitivities: sensitivities,
    coordinate: {
      x: cord(AxisType.EmployeeSender),
      y: {
        type: AxisType.ContentLabel,
        values: item.labels,
      },
      z: {
        type,
        values: [from],
      },
    },
    files_title: (entity, label_name) =>
      `<b>${sensitive} ${label_name}</b> files shared by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> shared <b>${sensitive} ${label_name}</b> information`,
      coordinate: (entity, label_id) => {
        return {
          x: cord3(AxisType.EmployeeSender, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: label_id ? [label_id] : item.labels,
          },
          z: {
            type,
            values: [from],
          },
        };
      },
    },
  };
};

const to_employees = (
  item: any,
  sensitivities: number[]
): EntitiesModalProps => {
  const { type, from } = SharedState.selectedTimeFrameOption.value;
  const sensitive = sensitivities_str(sensitivities);
  return {
    info_types: [item.id],
    type: POPULATION_TYPE.EMPLOYEE,
    title: `Employees that received  <b>${sensitive}</b> <info_type> information`,
    filter_sensitivities: sensitivities,
    coordinate: {
      x: cord(AxisType.EmployeeReceiver),
      y: {
        type: AxisType.ContentLabel,
        values: item.labels,
      },
      z: {
        type,
        values: [from],
      },
    },
    files_title: (entity, label_name) =>
      `<b>${sensitive} ${label_name}</b> files received by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> received <b>${sensitive} ${label_name}</b> information`,
      coordinate: (entity, label_id) => {
        return {
          x: cord3(AxisType.EmployeeReceiver, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: label_id ? [label_id] : item.labels,
          },
          z: {
            type,
            values: [from],
          },
        };
      },
    },
  };
};

const to_external_access = (
  item: any,
  sensitivities: number[]
): EntitiesModalProps => {
  const { type, from } = SharedState.selectedTimeFrameOption.value;
  const sensitive = sensitivities_str(sensitivities);
  return {
    info_types: [item.id],
    type: POPULATION_TYPE.CONTACT,
    title: `External Contacts that received <b>${sensitive}</b> <info_type> information`,
    filter_sensitivities: sensitivities,
    coordinate: {
      x: cord(AxisType.ExternalReceiver),
      y: {
        type: AxisType.ContentLabel,
        values: item.labels,
      },
      z: {
        type,
        values: [from],
      },
    },
    files_title: (entity, label_name) =>
      `<b>${sensitive} ${label_name}</b> files received by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> received <b>${sensitive} ${label_name}</b> information`,
      coordinate: (entity, label_id) => {
        return {
          x: cord3(AxisType.ExternalReceiver, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: label_id ? [label_id] : item.labels,
          },
          z: {
            type,
            values: [from],
          },
        };
      },
    },
  };
};

const to_organizations = (
  item: any,
  sensitivities: number[]
): EntitiesModalProps => {
  const { type, from } = SharedState.selectedTimeFrameOption.value;
  const sensitive = sensitivities_str(sensitivities);
  return {
    info_types: [item.id],
    type: POPULATION_TYPE.ORGANIZATION,
    title: `Organizations that received <b>${sensitive}</b> <info_type> information`,
    filter_sensitivities: sensitivities,
    coordinate: {
      x: cord(AxisType.OrganizationReceiver),
      y: {
        type: AxisType.ContentLabel,
        values: item.labels,
      },
      z: {
        type,
        values: [from],
      },
    },
    files_title: (entity, label_name) =>
      `<b>${sensitive} ${label_name}</b> files received by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> received <b>${sensitive} ${label_name}</b> information`,
      coordinate: (entity, label_id) => {
        return {
          x: cord3(AxisType.OrganizationReceiver, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: label_id ? [label_id] : item.labels,
          },
          z: {
            type,
            values: [from],
          },
        };
      },
    },
  };
};

export const get_modal_props = (
  item: any,
  sensitivities: number[],
  type: AxisType
): EntitiesModalProps => {
  const d = {
    [AxisType.EmployeeReceiverType]: "",
  };
  switch (type) {
    case AxisType.FolderType:
      return from_cloud_folders(item, sensitivities);
    case AxisType.EmployeeReceiverType:
      return to_employees(item, sensitivities);
    case AxisType.OrganizationReceiverType:
      return to_organizations(item, sensitivities);
    case AxisType.EmployeeSenderType:
      return from_employees(item, sensitivities);
    case AxisType.ExternalReceiverType:
    default:
      return to_external_access(item, sensitivities);
  }
};
